import React, { useContext } from 'react';
import { RichTextEl } from '../Global/RichText';
import {
    navIconCard,
    navRichTextCard,
    navContentCard,
    navStandardLink,
    NavGroup,
} from './index';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import {
    getLocalIcon,
    getMediaData,
    getMultiChoice,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    getDisplayDimensions,
} from '../Global/DataUtils';
import LazyImage from '../Global/LazyImage';
import {
    CTAConfiguration,
    KontentIcon,
    KontentStringValue,
} from '../../../types/KontentProps';
//@ts-ignore
import * as styles from './navigation.module.css';
import { AuthorStyles } from './index';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import { prefixWithLang } from '../../global/countries';

const getCta = (data: CTAConfiguration) => {
    return {
        label: data.cta_configuration__cta_label?.value,
        location: BuildLinkHref(
            data.cta_configuration__cta_location,
            data.cta_configuration__cta_location_anchor_point,
            true
        ),
        target:
            getMultiChoice(data.cta_configuration__cta_window_action) ===
            'same_window'
                ? '_self'
                : '_blank',
    };
};

export const renderNavIconCard = (
    link: navIconCard,
    authorStyles: AuthorStyles,
    panelName?: KontentStringValue
) => {
    const pageMetadata = useContext(PageMetadataContext);
    if (!link?.elements) return null;

    const cta = getCta(link.elements as CTAConfiguration);
    const iconData = getLocalIcon(link.elements.icon as KontentIcon);
    const preferredLanguage = pageMetadata?.preferredLanguage || 'default';
    const linkHref = prefixWithLang(cta.location, preferredLanguage);
    return (
        <div className={styles.iconCard}>
            <a
                id={`navigation__${panelName?.value?.replace(
                    / /g,
                    '-'
                )}__${cta?.label?.replace(/ /g, '-')}`}
                href={linkHref}
                target={cta.target}
                rel={
                    cta.target === '_blank' ? 'noopener noreferrer' : undefined
                }
                style={{ color: authorStyles.accentColor }}
            >
                {iconData?.url && (
                    <picture data-kontent-element-codename="icon">
                        {/* Icon box is fixed by CSS (.iconCard img: width 100%,
                        height 3.5rem); icon fragment has no width/height. */}
                        <LazyImage
                            src={iconData.url ?? ''}
                            alt={iconData.description || iconData.name || ''}
                            width={56}
                            height={56}
                        />
                    </picture>
                )}
                <span data-kontent-element-codename="cta_configuration__cta_label">
                    {cta.label}
                </span>
            </a>
        </div>
    );
};

export const renderRichTextCard = (
    card: navRichTextCard,
    panelName: KontentStringValue | undefined
) => {
    if (!card?.elements) return null;

    return (
        <div className={styles.richTextCard}>
            <RichTextEl
                richText={card.elements?.content}
                giveID={true}
                panelName={panelName}
                elementCodename="content"
            />
        </div>
    );
};

export const renderContentCard = (
    card: navContentCard,
    authorStyles: AuthorStyles,
    panelName?: KontentStringValue
) => {
    const pageMetadata = useContext(PageMetadataContext);
    if (!card?.elements) return null;

    const { description, headline } = card.elements;
    const cta = getCta(card.elements);
    const image = getMediaData(card.elements.image);
    const preferredLanguage = pageMetadata?.preferredLanguage || 'default';
    const linkHref = prefixWithLang(cta.location, preferredLanguage);
    return (
        <>
            <div className={styles.contentCard}>
                <a
                    id={`navigation__${panelName?.value?.replace(
                        / /g,
                        '-'
                    )}__${headline?.value?.replace(/ /g, '-')}`}
                    href={linkHref}
                    target={cta.target}
                    rel={
                        cta.target === '_blank'
                            ? 'noopener noreferrer'
                            : undefined
                    }
                    className={styles.contentCardLink}
                >
                    {image?.url && (
                        <div data-kontent-element-codename="image">
                            <LazyImage
                                {...getDisplayDimensions(
                                    image.width,
                                    image.height,
                                    800
                                )}
                                src={optimizeKontentImageUrl(image.url, {
                                    w: 800,
                                    fm: 'webp' as const,
                                    q: 95,
                                })}
                                srcSet={buildKontentSrcSet(image.url, 800, {
                                    fm: 'webp' as const,
                                    q: 95,
                                })}
                                sizes="(max-width: 991px) 100vw, 400px"
                                alt={image?.description ?? ''}
                            />
                        </div>
                    )}
                    <div
                        className={styles.headline}
                        style={{ color: authorStyles.accentColor }}
                        data-kontent-element-codename="headline"
                    >
                        {headline?.value}
                    </div>
                    <div className={styles.description}>
                        <RichTextEl
                            richText={description}
                            elementCodename="description"
                        />
                    </div>
                    <div
                        className={styles.cta}
                        data-kontent-element-codename="cta_configuration__cta_label"
                    >
                        {cta.label}
                    </div>
                </a>
            </div>
        </>
    );
};

export const renderStandardLink = (
    link: navStandardLink,
    panelName?: KontentStringValue
) => {
    const pageMetadata = useContext(PageMetadataContext);
    if (!link?.elements) return null;

    const cta = getCta(link.elements as CTAConfiguration);
    const preferredLanguage = pageMetadata?.preferredLanguage || 'default';
    const linkHref = prefixWithLang(cta.location, preferredLanguage);
    return (
        <div className={styles.standardLink}>
            <a
                id={
                    panelName?.value
                        ? `navigation__${panelName.value}__${cta.label}`?.replace(
                              / /g,
                              '-'
                          )
                        : undefined
                }
                href={linkHref}
                target={cta.target}
                rel={
                    cta.target === '_blank' ? 'noopener noreferrer' : undefined
                }
            >
                <span data-kontent-element-codename="cta_configuration__cta_label">
                    {cta.label}
                </span>
            </a>
        </div>
    );
};
